import React, { Children, Fragment, Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { ArrowDown } from '@jsluna/icons';
import { filterProps, getUniqueId } from '@jsluna/utils';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;
  subClass.__proto__ = superClass;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return self;
}

/**
 * Represents a single item in an accordion.
 * Wrap in [`Accordion`](#/Components/Accordion?id=accordion-1) for state management.
 */

var AccordionItem = function AccordionItem(_ref) {
  var Element = _ref.element,
      children = _ref.children,
      className = _ref.className,
      title = _ref.title,
      Heading = _ref.titleElement,
      defaultOpen = _ref.defaultOpen,
      propsOpen = _ref.open,
      standalone = _ref.standalone,
      id = _ref.id,
      onClick = _ref.onClick,
      respondAt = _ref.respondAt,
      color = _ref.color,
      rest = _objectWithoutPropertiesLoose(_ref, ["element", "children", "className", "title", "titleElement", "defaultOpen", "open", "standalone", "id", "onClick", "respondAt", "color"]);

  var titleId = id;
  var bodyId = id + "-body";
  var open = propsOpen !== undefined ? propsOpen : defaultOpen;
  return /*#__PURE__*/React.createElement(Element, _extends({}, filterProps(rest, ['multipleOpen']), {
    className: classnames(!respondAt && 'ln-c-accordion', respondAt && "ln-c-accordion@max-" + respondAt, open && 'is-open', standalone && 'ln-c-accordion--standalone', color && "ln-c-accordion--" + color, className)
  }), /*#__PURE__*/React.createElement("div", {
    className: "ln-c-accordion__head"
  }, /*#__PURE__*/React.createElement("button", {
    className: "ln-c-accordion__toggle",
    type: "button",
    onClick: onClick && function () {
      return onClick(open);
    },
    "aria-controls": bodyId,
    "aria-expanded": open,
    "aria-labelledby": titleId
  }, /*#__PURE__*/React.createElement(ArrowDown, {
    className: "ln-c-accordion__icon"
  })), /*#__PURE__*/React.createElement(Heading, {
    className: "ln-c-accordion__title",
    id: titleId
  }, title)), /*#__PURE__*/React.createElement("div", {
    className: "ln-c-accordion__body",
    role: "region",
    "aria-labelledby": titleId,
    id: bodyId
  }, (!!respondAt || open) && children));
};

AccordionItem.propTypes = {
  element: PropTypes.oneOfType([PropTypes.element, PropTypes.func, PropTypes.string]),
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  title: PropTypes.node.isRequired,
  titleElement: PropTypes.oneOfType([PropTypes.func, PropTypes.element, PropTypes.string]),
  defaultOpen: PropTypes.bool,
  open: PropTypes.bool,
  standalone: PropTypes.bool,
  id: PropTypes.string,
  onClick: PropTypes.func,

  /** Use to only show the content as an accordion until the specified breakpoint is reached */
  respondAt: PropTypes.string,
  color: PropTypes.oneOf(['light', 'dark'])
};
AccordionItem.defaultProps = {
  element: 'div',
  className: undefined,
  titleElement: 'h4',
  defaultOpen: false,
  open: undefined,
  standalone: false,
  id: '',
  onClick: undefined,
  respondAt: undefined,
  color: undefined
};
AccordionItem.displayName = 'AccordionItem';

var Accordion = /*#__PURE__*/function (_Component) {
  _inheritsLoose(Accordion, _Component);

  function Accordion(props) {
    var _this;

    _this = _Component.call(this, props) || this;
    _this.handleClick = _this.handleClick.bind(_assertThisInitialized(_this));
    _this.state = {
      hasInteracted: false,
      openItems: {}
    };
    _this.uuid = "ln-accordion-" + getUniqueId();
    return _this;
  }

  var _proto = Accordion.prototype;

  _proto.getChildren = function getChildren() {
    var _this$props = this.props,
        title = _this$props.title,
        id = _this$props.id,
        children = _this$props.children;

    if (title) {
      return /*#__PURE__*/React.createElement(AccordionItem, _extends({}, this.props, {
        id: this.getItemId(id, 0)
      }));
    }

    return children;
  };

  _proto.getItemId = function getItemId(itemId, index) {
    return itemId || this.uuid + "-" + index;
  };

  _proto.handleClick = function handleClick(itemId, open) {
    var multipleOpen = this.props.multipleOpen;
    this.setState(function (state) {
      var _Object$assign, _ref;

      return {
        hasInteracted: true,
        openItems: multipleOpen ? Object.assign({}, state.openItems, (_Object$assign = {}, _Object$assign[itemId] = !open, _Object$assign)) : (_ref = {}, _ref[itemId] = !open, _ref)
      };
    });
  };

  _proto.render = function render() {
    var _this2 = this;

    var _this$state = this.state,
        openItems = _this$state.openItems,
        hasInteracted = _this$state.hasInteracted;
    var _this$props2 = this.props,
        multipleOpen = _this$props2.multipleOpen,
        titleElement = _this$props2.titleElement,
        standalone = _this$props2.standalone,
        respondAt = _this$props2.respondAt,
        color = _this$props2.color;
    var itemProps = {
      titleElement: titleElement,
      standalone: standalone,
      respondAt: respondAt,
      color: color
    };
    var children = Children.map(this.getChildren(), function (child, index) {
      var id = _this2.getItemId(child.props.id, index);

      var open = openItems[id];

      if (!multipleOpen && hasInteracted) {
        open = open || false;
      }

      return React.cloneElement(child, Object.assign({}, itemProps, {
        id: id,
        open: open,
        onClick: function onClick(isOpen) {
          return _this2.handleClick(id, isOpen);
        }
      }));
    });
    return /*#__PURE__*/React.createElement(Fragment, null, children);
  };

  return Accordion;
}(Component);

Accordion.propTypes = {
  /** Array of `AccordionItems` or body contents if `title` prop set i.e. single accordion mode */
  children: PropTypes.node,

  /** Allow multipleOpen items to be open at one time */
  multipleOpen: PropTypes.bool,

  /** Element to use as title */
  titleElement: PropTypes.oneOfType([PropTypes.func, PropTypes.element, PropTypes.string]),

  /** Set to `true` to add spacing between items */
  standalone: PropTypes.bool,

  /** @see [`AccordionItem`](#/Components?id=accordion-item) */
  title: PropTypes.node,

  /** @see [`AccordionItem`](#/Components?id=accordion-item) */
  className: PropTypes.string,

  /** @see [`AccordionItem`](#/Components?id=accordion-item) */
  defaultOpen: PropTypes.bool,

  /** @see [`AccordionItem`](#/Components?id=accordion-item) */
  open: PropTypes.bool,

  /** @see [`AccordionItem`](#/Components?id=accordion-item) */
  id: PropTypes.string,

  /** @see [`AccordionItem`](#/Components?id=accordion-item) */
  onClick: PropTypes.func,

  /** @see [`AccordionItem`](#/Components?id=accordion-item) */
  respondAt: PropTypes.string,

  /** @see [`AccordionItem`](#/Components?id=accordion-item) */
  color: PropTypes.string
};
Accordion.defaultProps = {
  children: undefined,
  multipleOpen: false,
  titleElement: undefined,
  standalone: false,
  title: undefined,
  className: undefined,
  defaultOpen: false,
  open: undefined,
  id: undefined,
  onClick: undefined,
  respondAt: undefined,
  color: undefined
};
Accordion.displayName = 'Accordion';

export { Accordion, AccordionItem };
//# sourceMappingURL=index.js.map



//////////////////
// WEBPACK FOOTER
// ./~/@jsluna/accordion/dist/index.js
// module id = 502
// module chunks = 0